import {
  useCallback,
  useEffect,
  useMemo,
  useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from "@mui/material";
import TextField from '../components/SearchableTextField';
import SidebarLayout from "../components/sidebarLayout";
import { exportAuditLogsCsv, getAuditLogs } from "../APIs/LogsAPI";
import { useSnackbar } from "../contexts/SnackBarContext";
import { useTranslation } from "react-i18next";
import { formatDateTime24 } from "../components/dateTimeDisplay";
import { localizedErrorMessage } from "../utils/italianErrorMessage";

const ACTIONS = [
  "",
  "CREATE",
  "UPDATE",
  "DELETE",
  "STATUS_CHANGE",
  "CANCEL",
  "UNAUTHORIZED_ATTEMPT",
  "LOGIN",
  "LOGOUT",
  "REGISTER",
  "REFRESH_TOKEN",
];

function parseDetails(detailsText) {
  if (!detailsText || typeof detailsText !== "string") return {};
  try {
    return JSON.parse(detailsText);
  } catch (_) {
    return {};
  }
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return formatDateTime24(value);
}

function getUserName(row, details) {
  const direct = [row?.user?.name, row?.user?.surname].filter(Boolean).join(" ").trim();
  if (direct) return direct;

  const snapshot = [details?.userSnapshot?.name, details?.userSnapshot?.surname]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (snapshot) return snapshot;

  return "-";
}

function getUserEmail(row, details) {
  return row?.user?.email || details?.userSnapshot?.email || "-";
}

export default function LogsPage() {
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [filters, setFilters] = useState({
    action: "",
    entity: "",
    userId: "",
    startDate: "",
    endDate: "",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);

  const requestFilters = useMemo(() => ({
    action: filters.action || undefined,
    entity: filters.entity.trim() || undefined,
    userId: filters.userId.trim() || undefined,
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
  }), [filters]);

  const loadLogs = useCallback(async () => {
    if (!token || !tenant) return;

    setLoading(true);
    setError("");
    try {
      const result = await getAuditLogs({
        token,
        tenant,
        filters: requestFilters,
        page: page + 1,
        limit: rowsPerPage,
      });
      setRows(result.logs || []);
      setTotal(result.total || 0);
    } catch (err) {
      const message = localizedErrorMessage(err, t("logsPage.loadError"));
      setError(message);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [token, tenant, requestFilters, page, rowsPerPage, t]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleFilterChange = (key) => (event) => {
    const value = event.target.value;
    setPage(0);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleExport = async () => {
    try {
      await exportAuditLogsCsv({
        token,
        tenant,
        filters: requestFilters,
        page: 1,
        pageSize: 1000,
      });
      showSnackbar(t("logsPage.exportSuccess"), "success");
    } catch (err) {
      showSnackbar(t("logsPage.exportError"), "error");
    }
  };

  return (
    <SidebarLayout className="admin-responsive-page">
      <Container maxWidth={false} sx={{ px: { xs: 1.5, md: 3 }, py: { xs: 1.5, md: 2 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1.5} sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            {t("logsPage.title")}
          </Typography>
          <Button variant="outlined" onClick={handleExport}>
            {t("logsPage.exportCsv")}
          </Button>
        </Stack>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              select
              label={t("logsPage.action")}
              value={filters.action}
              onChange={handleFilterChange("action")}
              sx={{ minWidth: { xs: 0, md: 220 }, width: { xs: "100%", md: "auto" } }}
            >
              <MenuItem value="">{t("logsPage.all")}</MenuItem>
              {ACTIONS.filter(Boolean).map((action) => (
                <MenuItem key={action} value={action}>
                  {action}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label={t("logsPage.entity")}
              value={filters.entity}
              onChange={handleFilterChange("entity")}
              sx={{ minWidth: { xs: 0, md: 220 }, width: { xs: "100%", md: "auto" } }}
            />

            <TextField
              label={t("logsPage.userId")}
              value={filters.userId}
              onChange={handleFilterChange("userId")}
              sx={{ minWidth: { xs: 0, md: 160 }, width: { xs: "100%", md: "auto" } }}
            />

            <TextField
              label={t("logsPage.from")}
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange("startDate")}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label={t("logsPage.to")}
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange("endDate")}
              InputLabelProps={{ shrink: true }}
            />

            <Button variant="contained" onClick={loadLogs}>
              {t("logsPage.refresh")}
            </Button>
          </Stack>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper>
          <TableContainer className="admin-responsive-table">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>{t("logsPage.dateTime")}</TableCell>
                  <TableCell>{t("logsPage.action")}</TableCell>
                  <TableCell>{t("logsPage.entity")}</TableCell>
                  <TableCell>{t("logsPage.entityId")}</TableCell>
                  <TableCell>{t("logsPage.userId")}</TableCell>
                  <TableCell>{t("logsPage.userName")}</TableCell>
                  <TableCell>{t("logsPage.email")}</TableCell>
                  <TableCell>{t("logsPage.path")}</TableCell>
                  <TableCell>{t("logsPage.reason")}</TableCell>
                  <TableCell>{t("logsPage.requestId")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11}>
                      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={26} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11}>{t("logsPage.noLogs")}</TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const details = parseDetails(row.details);
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                        <TableCell>{row.action || "-"}</TableCell>
                        <TableCell>{row.entity || "-"}</TableCell>
                        <TableCell>{row.entityId ?? "-"}</TableCell>
                        <TableCell>{row.userId ?? "-"}</TableCell>
                        <TableCell sx={{ maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {getUserName(row, details)}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {getUserEmail(row, details)}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {details.path || "-"}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {details.reason || "-"}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {details.requestId || "-"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Paper>
      </Container>
    </SidebarLayout>
  );
}
